import React, { useState, useEffect } from 'react';
import { groupService, Group } from '../services/groupService';
import Navigation from '../components/Navigation';
import '../styles/Dashboard.css';
import '../styles/Groups.css';

const Groups: React.FC = () => {
  const [groups, setGroups] = useState<Group[]>([]);
  const [parentGroups, setParentGroups] = useState<Group[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());

  useEffect(() => {
    loadGroups();
  }, []);

  const loadGroups = async () => {
    setIsLoading(true);
    setError('');

    try {
      const [groupsRes, parentGroupsRes] = await Promise.all([
        groupService.getGroups(),
        groupService.getParentGroups(),
      ]);

      if (groupsRes.success) {
        setGroups(groupsRes.data);
      }

      if (parentGroupsRes.success) {
        setParentGroups(parentGroupsRes.data);
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Gruplar yüklenirken hata oluştu');
    } finally {
      setIsLoading(false);
    }
  };

  const toggleGroup = (groupName: string) => {
    setExpandedGroups(prev => {
      const newSet = new Set(prev);
      if (newSet.has(groupName)) {
        newSet.delete(groupName);
      } else {
        newSet.add(groupName);
      }
      return newSet;
    });
  };

  const toggleAll = () => {
    if (expandedGroups.size === parentGroups.length) {
      // Hepsi açık, hepsini kapat
      setExpandedGroups(new Set());
    } else {
      // Hepsini aç
      setExpandedGroups(new Set(parentGroups.map(g => g.name)));
    }
  };

  const getSubGroups = (parentGroupName: string) => {
    return groups.filter(g => g.parentGroup === parentGroupName);
  };

  const handleApproveGroup = async (groupId: string) => {
    if (!window.confirm('Bu AI tarafından oluşturulan grubu onaylamak istediğinize emin misiniz? Bu grup artık öneri sisteminde kullanılabilecek.')) {
      return;
    }

    try {
      const response = await groupService.approveGroup(groupId);
      if (response.success) {
        await loadGroups(); // Grupları yeniden yükle
        alert('Grup başarıyla onaylandı!');
      }
    } catch (error: any) {
      console.error('Grup onaylama hatası:', error);
      alert('Grup onaylanırken hata oluştu: ' + (error.response?.data?.message || error.message));
    }
  };

  const handleRejectGroup = async (groupId: string) => {
    if (!window.confirm('Bu AI tarafından oluşturulan grubu reddetmek ve silmek istediğinize emin misiniz?')) {
      return;
    }

    try {
      const response = await groupService.rejectGroup(groupId);
      if (response.success) {
        await loadGroups(); // Grupları yeniden yükle
        alert('Grup reddedildi ve silindi.');
      }
    } catch (error: any) {
      console.error('Grup reddetme hatası:', error);
      alert('Grup reddedilirken hata oluştu: ' + (error.response?.data?.message || error.message));
    }
  };

  return (
    <div className="container">
      <Navigation />

      {isLoading && (
        <div className="loading">
          <div className="spinner"></div>
          <p>Gruplar yükleniyor...</p>
        </div>
      )}

      {error && (
        <div className="error-message">
          <span className="error-icon">⚠️</span>
          <span>{error}</span>
        </div>
      )}

      {!isLoading && !error && (
        <>
          <div className="groups-header">
            <div>
              <h3>Grup Hiyerarşisi</h3>
              <p className="groups-stats">
                {parentGroups.length} Üst Grup • {groups.length} Alt Grup
              </p>
            </div>
            <button 
              className="btn btn-secondary toggle-all-btn"
              onClick={toggleAll}
            >
              {expandedGroups.size === parentGroups.length ? '▼ Hepsini Kapat' : '▶ Hepsini Aç'}
            </button>
          </div>

          <div className="accordion-container">
            {parentGroups.map((parentGroup) => {
              const subGroups = getSubGroups(parentGroup.name);
              const isExpanded = expandedGroups.has(parentGroup.name);

              return (
                <div key={parentGroup._id || parentGroup.name} className="accordion-item">
                  <div 
                    className={`accordion-header ${isExpanded ? 'expanded' : ''}`}
                    onClick={() => toggleGroup(parentGroup.name)}
                  >
                    <div className="accordion-title">
                      <span className="accordion-icon">
                        {isExpanded ? '▼' : '▶'}
                      </span>
                      <span className="parent-group-name">{parentGroup.name}</span>
                      <span className="sub-group-count">
                        ({subGroups.length} alt grup)
                      </span>
                    </div>
                  </div>

                  {isExpanded && (
                    <div className="accordion-content">
                      {subGroups.length > 0 ? (
                        <ul className="sub-groups-list">
                          {subGroups.map((subGroup, index) => {
                            const isAIGenerated = subGroup.isAIGenerated && subGroup.needsApproval && !subGroup.approved;
                            return (
                              <li 
                                key={subGroup._id || index} 
                                className={`sub-group-item ${isAIGenerated ? 'ai-generated' : ''}`}
                              >
                                <span className="sub-group-bullet">•</span>
                                <span className="sub-group-name">{subGroup.name}</span>
                                {isAIGenerated && (
                                  <span className="ai-badge" title="AI tarafından oluşturuldu - Onay bekliyor">
                                    🤖 AI
                                  </span>
                                )}
                                {isAIGenerated && (
                                  <div className="group-actions">
                                    <button
                                      className="btn-icon btn-approve"
                                      onClick={() => handleApproveGroup(subGroup._id!)}
                                      title="Onayla"
                                    >
                                      ✓
                                    </button>
                                    <button
                                      className="btn-icon btn-reject"
                                      onClick={() => handleRejectGroup(subGroup._id!)}
                                      title="Reddet"
                                    >
                                      ✗
                                    </button>
                                  </div>
                                )}
                              </li>
                            );
                          })}
                        </ul>
                      ) : (
                        <div className="no-subgroups">
                          Alt grup bulunamadı
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          <footer>
            <p>© 2025 Qualisu Paneli | Toplam Grup: {groups.length + parentGroups.length}</p>
          </footer>
        </>
      )}
    </div>
  );
};

export default Groups;

